import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDocumentTitle } from '@/lib/hooks';
import styles from './Placeholder.module.css';

/**
 * Segnaposto onesto per le rotte non ancora costruite.
 * ---------------------------------------------------------------------------
 * Serve a due cose:
 *  1. la struttura delle URL è verificabile SUBITO (i 301 puntano a rotte che
 *     esistono, non a 404);
 *  2. la pagina DICE che è un segnaposto. Un contenitore vuoto con un titolo
 *     plausibile è indistinguibile da una pagina rotta.
 *
 * Ogni segnaposto dichiara da dove viene il contenuto originale, così chi la
 * implementa non deve cercarlo.
 */
export interface PlaceholderProps {
  readonly title: string;
  /** Percorso della pagina statica di riferimento in ../infocomuni.it/. */
  readonly source: string;
  /** Note su cosa serve per completarla. */
  readonly notes?: readonly string[];
}

export function Placeholder({ title, source, notes = [] }: PlaceholderProps) {
  const { t } = useTranslation();
  useDocumentTitle(title);

  return (
    <div className={`ic-container ${styles.wrapper}`}>
      <h1 className={styles.title}>{title}</h1>
      <p className={styles.badge}>{t('page_placeholder', 'Pagina non ancora migrata')}</p>
      <p className={styles.body}>
        {t(
          'page_placeholder_body',
          'Questa rotta esiste ed è raggiungibile, ma il contenuto non è ancora stato portato dal portale statico. La struttura delle URL è già quella definitiva.',
        )}
      </p>
      <dl className={styles.meta}>
        <dt>Sorgente statica</dt>
        <dd>
          <code>{source}</code>
        </dd>
      </dl>
      {notes.length > 0 ? (
        <>
          <h2 className={styles.notesTitle}>Da fare</h2>
          <ul className={styles.notes}>
            {notes.map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        </>
      ) : null}
      <p>
        <Link to="/">{t('breadcrumb_home', 'Home')}</Link>
      </p>
    </div>
  );
}
